/**
 * Who the keyboard belongs to, for the two window-wide shortcut handlers (the canvas's
 * and the page's).
 *
 * The QA pass of 2026-09-24 found keys doing two things at once: Delete pressed in an
 * open menu deleted the shape behind it, and after dragging a slider the slider kept the
 * keyboard, so Cmd+Z, Delete and the tool keys did nothing and the arrows changed the
 * slider instead of nudging the shape.
 */

let openMenus = 0;

/** A menu says it is open, and hands back how to say it closed. */
export function holdKeyboardForMenu(): () => void {
  openMenus++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    openMenus--;
  };
}

/** While a menu is open, its keys are its own: nothing on the page behind it acts on them. */
export function menuIsOpen(): boolean {
  return openMenus > 0;
}

const TEXT_INPUTS = new Set(['text', 'search', 'number', 'email', 'password', 'url', 'tel', '']);
const SLIDER_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']);

/**
 * Whether this key belongs to the field it was typed in rather than to a shortcut.
 *
 * Only fields that take text keep every key. A slider keeps the keys that move it and
 * nothing else; a checkbox, a colour well or a button keeps none — being focused is not
 * the same as being typed into.
 */
export function keyBelongsToField(target: EventTarget | null, key: string): boolean {
  const field = target as { tagName?: string; isContentEditable?: boolean; type?: string } | null;
  if (!field || typeof field.tagName !== 'string') return false;
  if (field.isContentEditable || field.tagName === 'TEXTAREA' || field.tagName === 'SELECT') return true;
  if (field.tagName !== 'INPUT') return false;
  const type = field.type?.toLowerCase() ?? '';
  if (TEXT_INPUTS.has(type)) return true;
  if (type === 'range') return SLIDER_KEYS.has(key);
  return false;
}

const CONTROL_ROLES = new Set(['button', 'menuitem', 'menuitemcheckbox', 'menuitemradio', 'tab', 'option', 'switch', 'checkbox', 'radio']);

/**
 * Whether a focused control should keep this key: Return, Space and Escape on a button
 * (or anything playing one) are that button's to act on. The canvas used to take them —
 * Return on the focused title button started editing the selected text instead of
 * opening the menu, and Escape cleared the selection (QA 2026-09-24, files-doc-N5).
 */
export function keyBelongsToControl(target: EventTarget | null, key: string): boolean {
  const el = target as { tagName?: string; getAttribute?: (name: string) => string | null; closest?: (selector: string) => unknown } | null;
  if (!el || typeof el.tagName !== 'string') return false;
  // A segmented control steps its choice with the arrows; the canvas nudged the
  // selection with the same press (cloner-F5).
  if (key === 'ArrowLeft' || key === 'ArrowRight') return !!el.closest?.('[role="radiogroup"]');
  if (key !== 'Enter' && key !== ' ' && key !== 'Escape') return false;
  if (el.tagName === 'BUTTON' || el.tagName === 'A' || el.tagName === 'SUMMARY') return true;
  const role = el.getAttribute?.('role');
  return !!role && CONTROL_ROLES.has(role);
}

let typingOnCanvas = false;

/** Text on the canvas is being typed into: letters are words, not shortcuts. */
export function setTypingOnCanvas(on: boolean): void {
  typingOnCanvas = on;
}

export function typingIsOnCanvas(): boolean {
  return typingOnCanvas;
}

/**
 * Whether this is a Mac (or an iPad, which types like one). Shortcuts are written the
 * Mac's way throughout — ⌘, ⌥, ⇧, ⌃ — and on Windows and Linux ⌘ is Ctrl.
 */
export const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(((navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform || navigator.platform || navigator.userAgent));

/** ⌘ on a Mac, Ctrl everywhere else: the key a shortcut is held with. */
export function primaryKey(event: { metaKey: boolean; ctrlKey: boolean }): boolean {
  return IS_MAC ? event.metaKey : event.ctrlKey;
}

/**
 * The key that pairs with ⌘ in a ⌃⌘ shortcut: ⌃ on a Mac, Alt elsewhere — there, ⌃ and ⌘
 * are the same key, so ⌃⌘S could never be pressed on Windows (QA 2026-10-05, ART-DCK-06).
 */
export function secondKey(event: { ctrlKey: boolean; altKey: boolean }): boolean {
  return IS_MAC ? event.ctrlKey : event.altKey;
}

const WINDOWS_KEYS: Record<string, string> = { '⌫': 'Backspace', '⌦': 'Delete', '↩': 'Enter', '⎋': 'Esc' };

/**
 * A shortcut as this computer's keyboard says it: "⇧⌘E" on a Mac, "Ctrl+Shift+E" on
 * Windows. Written once the Mac's way wherever it is declared, and said here.
 */
export function keysFor(shortcut: string, mac = IS_MAC): string {
  return mac ? shortcut : (keyParts(shortcut, false) ?? [shortcut]).join('+');
}

/**
 * A shortcut as the keys pressed for it, one by one — ["Ctrl", "Shift", "E"], or on a Mac
 * ["⇧", "⌘", "E"] — so each can be drawn as its own key. Null for what is not a shortcut:
 * a menu's right-hand column also holds sizes ("210 × 297 mm"), which are said as words.
 */
export function keyParts(shortcut: string, mac = IS_MAC): string[] | null {
  if (!shortcut || /\s/.test(shortcut)) return null;
  let rest = shortcut;
  const marks: string[] = [];
  let command = false;
  let control = false;
  let option = false;
  let shift = false;
  for (;;) {
    const mark = rest[0];
    if (mark === '⌘') command = true;
    else if (mark === '⌃') control = true;
    else if (mark === '⌥') option = true;
    else if (mark === '⇧') shift = true;
    else break;
    marks.push(mark);
    rest = rest.slice(1);
  }
  if (!rest) return null;
  if (mac) return [...marks, rest];
  // ⌃⌘ together is Ctrl+Alt (`secondKey`); either alone is Ctrl. Windows says them in the
  // order Ctrl, Alt, Shift.
  const ctrl = command || control;
  const alt = option || (command && control);
  return [...(ctrl ? ['Ctrl'] : []), ...(alt ? ['Alt'] : []), ...(shift ? ['Shift'] : []), WINDOWS_KEYS[rest] ?? rest];
}
